import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, ShieldCheck } from "lucide-react";
import { CONTACT } from "@/lib/site";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.35 + i * 0.12 },
  }),
};

const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden">
    <motion.span variants={line} custom={i} initial="hidden" animate="show" className="block">
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero"
      className="relative min-h-screen w-full overflow-hidden grid-lines"
    >
      {/* Parallax image, right side, clipped */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute right-0 top-0 h-full w-full md:w-[52%] z-0"
      >
        <div className="relative h-full w-full">
          <img
            src="https://images.pexels.com/photos/558630/pexels-photo-558630.png"
            alt="Surveillance camera on a modern building"
            className="h-full w-full object-cover grayscale contrast-125 opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
        </div>
      </motion.div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-[1400px] flex-col justify-center px-5 sm:px-8 pt-28 pb-16">
        <motion.div style={{ y: textY }}>
          {/* Ajax badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            data-testid="ajax-badge"
            className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-cyanbrand/30 bg-cyanbrand/5 px-4 py-2"
          >
            <span className="h-2 w-2 rounded-full bg-cyanbrand animate-pulse" />
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyanbrand">
              Official partner · Ajax Security Systems
            </span>
          </motion.div>

          <h1 className="font-display text-[3.4rem] leading-[0.9] sm:text-7xl lg:text-[7.5rem] font-semibold tracking-tighter">
            <MaskLine i={0}>Security that</MaskLine>
            <MaskLine i={1}>
              <span className="text-stroke-accent">never</span> blinks.
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-8 max-w-xl font-body text-base md:text-lg text-white/60 leading-relaxed"
          >
            {CONTACT.brand} designs, installs and maintains CCTV and intelligent
            security systems for domestic & commercial properties — engineered by{" "}
            {CONTACT.parent}.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#quote"
              data-testid="hero-quote-cta"
              className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-4 font-semibold text-black transition-transform duration-200 hover:scale-[1.03]"
            >
              Book a free survey <ArrowDownRight className="h-4 w-4" />
            </a>
            <a
              href="#services"
              data-testid="hero-services-cta"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-4 font-semibold text-white transition-colors duration-200 hover:border-cyanbrand hover:text-cyanbrand"
            >
              Explore services
            </a>
          </motion.div>

          {/* stat strip */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.8 }}
            className="mt-16 flex flex-wrap gap-x-10 gap-y-4"
          >
            {[
              { k: "24/7", v: "Remote monitoring" },
              { k: "Grade 2", v: "Verified hardware" },
              { k: "2", v: "London & Berkshire" },
            ].map((s) => (
              <div key={s.v} className="flex items-center gap-3">
                <span className="font-display text-2xl font-semibold text-cyanbrand">{s.k}</span>
                <span className="font-mono text-[11px] uppercase tracking-wider text-white/40 max-w-[110px] leading-tight">
                  {s.v}
                </span>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      <div className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 items-center gap-2 md:flex">
        <ShieldCheck className="h-3.5 w-3.5 text-white/30" />
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/30">
          Scroll
        </span>
      </div>
    </section>
  );
};
